// @flow
import React from 'react';
import { get } from 'lodash';
import moment from 'moment';

import { getClassWithModifiers } from 'Components/utils/className/className.util';
import { ICONS } from 'Components/utils/icon/icons';

import { PricingTable } from 'Components/frameworks/pricing-table/PricingTable.jsx';
import { type IconName } from 'Components/frameworks/icon/Icon.jsx';

type PriceCalendarPriceDetailsProps = {
  labelDaySelection: string,
  closeButtonAltText: string,
  validateAction: () => void,
  closeAction: () => void,
  position: string,
  verticalAlignment: string,
  priceConfig: null | {
    bestPrice: FormattedPrice,
    initialPrice: FormattedPrice,
    unit: string,
    isBestPriceMonth: boolean,
    currency: string,
    termsAndConditions: {
      departureDate: number,
      duration: number,
      accommodationComfortLabel: string,
      numberOfAdults: number,
      departureCity: null | string,
    },
  },
  cmsPrice: {
    format: *,
  },
  cmsStartingPrice: *,
  isJoyview: boolean,
  shouldRenderInlinePopin: boolean,
  warningMessage: string,
};

function getButtonProps(display: boolean, label: string, onClick: () => void) {
  return display ? { children: label, onClick } : { children: null };
}

function getLabel(isJoyview: boolean, labelJoyView: string, labelDefault: string): string {
  return isJoyview ? labelJoyView : labelDefault;
}

function getIcons({ isJoyview, isTour, hasTransfer, hasTransport }): Array<IconName> {
  if (isJoyview || isTour) return [];

  const icons = [ICONS.ACCOMODATIONS, ICONS.BAR, ICONS.DISHES];

  if (hasTransfer) {
    icons.unshift(ICONS.TRANSFER);
  } else if (hasTransport) {
    icons.unshift(ICONS.TRANSPORT);
  } else {
    icons.unshift(ICONS.NO_TRANSPORT);
  }

  return icons;
}

function getPriceProps(priceConfig) {
  if (!priceConfig) {
    return {};
  }
  const { bestPrice, initialPrice, unit } = priceConfig;
  return {
    bestPrice,
    hasInitialPrice: !!initialPrice && !!initialPrice.value,
    initialPrice,
    unit,
  };
}

function getInfos(props) {
  const { cmsStartingPrice, priceConfig } = props;

  const infos = [];

  // departureDate
  const departureDate = moment(
    get(priceConfig, 'termsAndConditions.departureDate'),
    'YYYYMMDD',
  ).format(get(cmsStartingPrice, 'dateFormat', 'D MMMM YYYY'));

  const { departureDateLabel } = cmsStartingPrice;

  infos.push(`${departureDateLabel} ${departureDate}`);

  // durationFormatted
  infos.push(get(priceConfig, 'termsAndConditions.durationFormatted'));

  // departureCity
  const { departureCityLabel } = cmsStartingPrice;
  const departureCity = get(priceConfig, 'termsAndConditions.departureCity.name');

  if (departureCity) {
    infos.push(`${departureCityLabel} ${departureCity}`);
  }

  // accommodation
  infos.push(get(priceConfig, 'termsAndConditions.accommodationComfortLabel'));

  return infos.filter(info => info);
}

export function PriceCalendarPriceDetails(props: PriceCalendarPriceDetailsProps) {
  const {
    cmsStartingPrice,
    isJoyview,
    labelDaySelection,
    position,
    priceConfig,
    shouldRenderInlinePopin,
    validateAction,
    verticalAlignment,
    warningMessage,
  } = props;

  return (
    <div
      className={getClassWithModifiers('PriceCalendarPriceDetail', [position, verticalAlignment])}
    >
      <PricingTable
        button={getButtonProps(shouldRenderInlinePopin, labelDaySelection, validateAction)}
        icons={getIcons({
          isJoyview,
          isTour: false,
          hasTransfer: false,
          hasTransport: !!get(priceConfig, 'termsAndConditions.departureCity.value', false),
        })}
        infos={getInfos(props)}
        label={getLabel(isJoyview, cmsStartingPrice.roomOnly, cmsStartingPrice.allInclusive)}
        noPriceLabel={warningMessage}
        price={getPriceProps(priceConfig)}
      />
    </div>
  );
}
